
type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogCategory = 'table' | 'tracking' | 'api' | 'mutation' | 'query' | 'general';

interface LogOptions {
  category?: LogCategory;
  data?: unknown;
  devOnly?: boolean;
}

const isDevelopment = import.meta.env.DEV;

const CATEGORY_ICONS: Record<LogCategory, string> = {
  table: '📊',
  tracking: '🔍',
  api: '🌐',
  mutation: '💾',
  query: '🔄',
  general: '📝'
};

const LEVEL_STYLES: Record<LogLevel, string> = {
  debug: 'color: #9E9E9E',
  info: 'color: #2196F3',
  warn: 'color: #FF9800',
  error: 'color: #F44336; font-weight: bold'
};

function getTimestamp(): string {
  const now = new Date();
  return now.toLocaleTimeString('ru-RU', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit',
    fractionalSecondDigits: 3 
  });
}

function log(level: LogLevel, message: string, options: LogOptions = {}) {
  const { category = 'general', data, devOnly = true } = options;

  if (devOnly && !isDevelopment) {
    return;
  }

  const icon = CATEGORY_ICONS[category];
  const timestamp = getTimestamp();
  const prefix = `${icon} [${timestamp}]`;
  const style = LEVEL_STYLES[level];

  const formattedMessage = `%c${prefix} ${message}`;
  
  switch (level) {
    case 'debug':
      if (data !== undefined) {
        console.log(formattedMessage, style, data);
      } else {
        console.log(formattedMessage, style);
      } 
      break;
    case 'info':
      if (data !== undefined) {
        console.log(formattedMessage, style, data);
      } else {
        console.log(formattedMessage, style);
      }
      break;
    case 'warn':
      if (data !== undefined) {
        console.warn(formattedMessage, style, data);
      } else {
        console.warn(formattedMessage, style);
      }
      break;
    case 'error':
      if (data !== undefined) {
        console.error(formattedMessage, style, data);
      } else {
        console.error(formattedMessage, style);
      }
      break;
  }
}
  
export const logger = {
  debug: (message: string, options?: LogOptions) => {
    log('debug', message, options);
  },

  info: (message: string, options?: LogOptions) => {
    log('info', message, options);
  },

  warn: (message: string, options?: LogOptions) => {
    log('warn', message, { ...options, devOnly: false });
  },

  error: (message: string, options?: LogOptions) => {
    log('error', message, { ...options, devOnly: false });
  },

  table: {
    save: (tableId: string, changesCount: number) => {
      logger.info(`Сохранение таблицы: ${tableId}`, {
        category: 'table',
        data: { changesCount }
      });
    },

    saveSuccess: (tableId: string, response?: unknown) => {
      logger.info(`✅ Таблица сохранена: ${tableId}`, {
        category: 'table',
        data: response
      });
    },

    saveError: (tableId: string, error: unknown) => {
      logger.error(`❌ Ошибка сохранения таблицы: ${tableId}`, {
        category: 'table',
        data: error
      });
    },

    reload: (tableId: string) => {
      logger.info(`Перезагрузка таблицы: ${tableId}`, {
        category: 'table'
      });
    },

    reloadError: (tableId: string, error: unknown) => {
      logger.error(`❌ Ошибка перезагрузки таблицы: ${tableId}`, {
        category: 'table',
        data: error
      });
    },

    baselineReset: (tableId: string, rowCount?: number) => {
      logger.info(`🔄 Baseline сброшен: ${tableId}`, {
        category: 'table',
        data: { rowCount }
      });
    }
  },

  tracking: {
    change: (tableId: string, row: number, col: number, value: unknown) => {
      logger.info(`Изменение ячейки [${tableId}]`, {
        category: 'tracking',
        data: { row, col, value }
      });
    },

    changesCount: (tableId: string, count: number) => {
      logger.info(`Изменений в таблице [${tableId}]: ${count}`, {
        category: 'tracking'
      });
    },

    rowAdded: (tableId: string, rowIndex: number, rowId: string | number) => {
      logger.info(`➕ Строка добавлена [${tableId}]`, {
        category: 'tracking',
        data: { rowIndex, rowId }
      });
    },

    rowDeleted: (tableId: string, rowIndex: number, rowId: string | number) => {
      logger.info(`➖ Строка удалена [${tableId}]`, {
        category: 'tracking',
        data: { rowIndex, rowId }
      });
    },

    clear: (tableId: string) => {
      logger.info(`🧹 Изменения очищены [${tableId}]`, {
        category: 'tracking'
      });
    },

    apiUnavailable: (tableId: string) => {
      logger.warn(`TrackedTable API недоступен: ${tableId}`, {
        category: 'tracking'
      });
    },

    changeRegistered: (tableId: string, row: number, col: number, details: unknown) => {
      logger.debug(`🔄 Change tracking [${tableId}] R${row}C${col}`, {
        category: 'tracking',
        data: details
      });
    }
  },

  api: {
    request: (endpoint: string, method: string = 'GET') => {
      logger.debug(`API ${method}: ${endpoint}`, {
        category: 'api'
      });
    },

    response: (endpoint: string, data?: unknown) => {
      logger.debug(`API Response: ${endpoint}`, {
        category: 'api',
        data
      });
    },

    error: (endpoint: string, error: unknown) => {
      logger.error(`API Error: ${endpoint}`, {
        category: 'api',
        data: error
      });
    },

    fallback: (message: string) => {
      logger.warn(`API недоступен: ${message}`, {
        category: 'api'
      });
    }
  },

  mutation: {
    start: (key: string) => {
      logger.debug(`Mutation start: ${key}`, {
        category: 'mutation'
      });
    },

    success: (key: string, data?: unknown) => {
      logger.info(`✅ Mutation success: ${key}`, {
        category: 'mutation',
        data
      });
    },

    error: (key: string, error: unknown) => {
      logger.error(`❌ Mutation error: ${key}`, {
        category: 'mutation',
        data: error
      });
    }
  },

  query: {
    fetching: (key: string) => {
      logger.debug(`Query fetching: ${key}`, {
        category: 'query'
      });
    },

    success: (key: string, dataLength?: number) => {
      logger.debug(`Query success: ${key}`, {
        category: 'query',
        data: { dataLength }
      });
    },

    error: (key: string, error: unknown) => {
      logger.error(`Query error: ${key}`, {
        category: 'query',
        data: error
      });
    }
  }
};

export function logGroup(title: string, callback: () => void) {
  if (!isDevelopment) {
    callback();
    return;
  }

  console.group(`📦 ${title}`);
  try {
    callback();
  } finally {
    console.groupEnd();
  }
}

export function logTime<T>(label: string, callback: () => T): T {
  if (!isDevelopment) {
    return callback();
  }

  const start = performance.now();
  const result = callback();
  const end = performance.now();
  const duration = (end - start).toFixed(2);

  logger.debug(`⏱️ ${label}: ${duration}ms`, {
    category: 'general'
  });

  return result;
}

